/**
 * Živé odjezdy PID přes Golemio API (https://api.golemio.cz).
 * Vyžaduje bezplatný klíč (X-Access-Token) — env VITE_GOLEMIO_API_KEY,
 * nebo expertní nastavení (jen lokálně). Funguje pro zastávky s GTFS id
 * z PID feedu (importovaného přes „GTFS import").
 *
 * Vrstva je best-effort: chyby nikdy nepropagujeme jako pád, jen jako
 * prázdný výsledek — detail trasy pak živé odjezdy prostě neukáže.
 */

export interface LiveDeparture {
  /** Označení linky (např. „9", „S4"). */
  route: string;
  /** Cílová stanice spoje. */
  headsign: string;
  /** Plánovaný odjezd (ms epoch). */
  scheduledMs: number;
  /** Predikovaný odjezd (ms epoch). */
  predictedMs: number;
  /** Zpoždění v minutách (predikce − plán). */
  delayMin: number;
}

export function envGolemioKey(): string {
  try {
    return (import.meta.env?.VITE_GOLEMIO_API_KEY as string | undefined) ?? '';
  } catch {
    return '';
  }
}

/**
 * Živé odjezdy ze zastávky podle GTFS stop id (PID). Nikdy nevyhazuje.
 */
export async function fetchLiveDepartures(
  stopIds: string[],
  key: string,
  timeoutMs = 6000,
): Promise<LiveDeparture[]> {
  if (!key || stopIds.length === 0) return [];
  const params = new URLSearchParams({ limit: '6', minutesAfter: '180', order: 'real' });
  for (const id of stopIds.slice(0, 4)) params.append('ids[]', id);
  const url = `https://api.golemio.cz/v2/pid/departureboards?${params.toString()}`;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(url, {
      signal: ctrl.signal,
      headers: { 'X-Access-Token': key, Accept: 'application/json' },
    });
    if (!res.ok) return [];
    const data = (await res.json()) as {
      departures?: Array<{
        route?: { short_name?: string };
        trip?: { headsign?: string };
        departure_timestamp?: { scheduled?: string; predicted?: string };
        delay?: { minutes?: number; is_available?: boolean };
      }>;
    };
    const out: LiveDeparture[] = [];
    for (const d of data.departures ?? []) {
      const sched = Date.parse(d.departure_timestamp?.scheduled ?? '');
      const pred = Date.parse(d.departure_timestamp?.predicted ?? d.departure_timestamp?.scheduled ?? '');
      if (!Number.isFinite(sched)) continue;
      out.push({
        route: d.route?.short_name ?? '?',
        headsign: d.trip?.headsign ?? '',
        scheduledMs: sched,
        predictedMs: Number.isFinite(pred) ? pred : sched,
        delayMin: d.delay?.is_available && Number.isFinite(d.delay?.minutes) ? (d.delay!.minutes as number) : Math.round(((pred || sched) - sched) / 60_000),
      });
    }
    return out;
  } catch {
    return [];
  } finally {
    clearTimeout(timer);
  }
}
